import React, { useState, useEffect, useRef } from 'react';
import { getBackendURL } from '../utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import '../BasicReminder.css';
import ProgressTabs from '../components/ProgressTabs';
import SweatTextTitle from '../steps/SweatTextTitle';
import RemindersInfo from './RemindersInfo';

const stopSuffix = " Reply STOP to end messages.";
const maxMessageLength = 160 - stopSuffix.length; // 160 is typical SMS limit

const Step3 = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [trainerName, setTrainerName] = useState('Trainer Name'); // Default trainer name for preview
  const [syncing, setSyncing] = useState(false); // New state for syncing status
  const [synced, setSynced] = useState(false); // New state for synced status
  const [syncError, setSyncError] = useState(false); // New state for sync error
  const navigate = useNavigate();
  const debounceTimeout = useRef(null); // Add debounce timeout ref

  const steps = ['Step 1', 'Step 2', 'Step 3', 'Step 4', 'Step 5', 'Step 6'];

  useEffect(() => {

    if ((!user) || (!user.role.includes('trainer'))) {
      alert('You must be a trainer to access this step. If you are indeed a trainer, please contact our Instagram for account support.');
      navigate('/tools/reminders/step1');
      return;
    }

  }, []);


  // a useEffect to post a new trainer_info for our user if one does not exist.
  // Once it succeeds, refresh the page.
  useEffect(() => {

    if (!user) return;
    
    const postTrainerInfo = async () => {

      if (!user.id) {
        console.error('User ID is not available');
        return;
      }
      
      try {
        const res = await fetch(getBackendURL() + '/trainer_info/?user_id=' + user.id, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          },
          body: JSON.stringify({ user_id: user.id }),
        });

        if (res.ok) {
          window.location.reload();
        } else {
          throw new Error('Failed to create trainer info');
        }
      } catch (err) {
        console.error('Error posting trainer_info:', err);
        alert('Failed to create trainer info. Please try again later.');
      }
    };

    if (!user.trainer_info){
      postTrainerInfo();
    } 
    else {
      setTrainerName(user.trainer_info.name || 'Trainer Name'); // Use existing trainer name
      setLoading(false);
      setMessage(user.trainer_info.custom_sms_message_text || '');
    }
  }, [user]);


  const handleMessageChange = (value) => {
    setSyncing(true);
    setSynced(false);
    setSyncError(false);
    setMessage(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      saveMessage(value);
    }, 1000);
  };

  const saveMessage = async (message) => {
    try {
      const res = await fetch(getBackendURL() + '/trainer_info/' + user.trainer_info.id, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
        },
        body: JSON.stringify({ custom_sms_message_text: message }),
      });

      if (!res.ok) throw new Error('Failed to save reminder message');
      setSynced(true);
    } catch (err) {
      console.error('Error saving reminder message:', err);
      setSyncError(true);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="reminder-container fogged-card">


      <SweatTextTitle />
      <RemindersInfo />

      
      <ProgressTabs currentStep={2} />


      <h2 className="step-title">Step 3: Customize the reminder text</h2>
      <p>
        Here you can customize the reminder text that will be sent to your clients.
      </p>
      <p>
        Some parts of the message are automatically filled in with updated values, such as the trainer's name and the appointment time,
        as shown below.
      </p>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div
            className="sms-preview"
          >
            Reminder of your appointment with {user.name} at 10:00 AM.
            <br />
            <textarea
              value={message}
              onChange={(e) => handleMessageChange(e.target.value)}
              placeholder="...Add more to customize your reminder message here..."
              maxLength={maxMessageLength}
              style={{
                width: '95%',
                height: '60px',
                backgroundColor: '#2bcd41',
                color: 'darkgreen',
                border: '1px solid #28a745',
                borderRadius: '4px',
                padding: '0.5rem',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
              }}
            />
            {stopSuffix}
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.9rem', color: message.length >= maxMessageLength ? '#c00' : '#888' }}>
            {message.length}/{maxMessageLength} characters
          </div>
        </>
      )}

      {syncing ? (
        <span className="syncing-indicator" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '1rem' }}>
          <span className="spinner" style={{
            width: '1rem',
            height: '1rem',
            border: '2px solid #ccc',
            borderTop: '2px solid #000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginRight: '0.5rem',
          }}></span>
          Syncing...
        </span>
      ) : synced ? (
        <span style={{ color: 'green', fontWeight: 'bold', marginLeft: '1rem' }}>✔ Synced</span>
      ) : syncError ? (
        <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '1rem' }}>✘ Failed</span>
      ) : null}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <button className="ptc-secondary-button" onClick={() => navigate('/tools/reminders/step2')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Previous Step
        </button>
        <button className="ptc-button" onClick={() => navigate('/tools/reminders/step4')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Next Step
        </button>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Step3;